import { useEffect, useState } from 'react';

import {
  type AppointmentModeTypesEnum,
  AppointmentModeTypesLookup,
  type AppointmentStatusEnum,
  AppointmentStatusTypesLookup,
  type WorkerStatusTypesEnum,
  WorkerStatusTypesLookup,
} from '@eluve/graphql-types';

import { useAppointmentActivity } from '../useAppointmentActivity';
import { useAppointmentMode } from '../useAppointmentMode';
import { useAppointmentStatus } from '../useAppointmentStatus';
import { useSummary } from '../useSummary';

const STUCK_TIMEOUT_MS = 10_000;
// ~50% over the p95 end-to-end summarization runtime — past this, something
// is genuinely slow regardless of which pipeline is handling the job.
const SUMMARIZATION_TAKING_LONGER_TIMEOUT_MS = 90_000;

export type AppointmentSummaryDisplayKind =
  | 'manual-charting'
  | 'existing-note'
  | 'stuck'
  | 'pending-audio'
  | 'summarizing-taking-longer'
  | 'pending-transcript'
  | 'summarizing'
  | 'failed-summary'
  | 'fallback';

export type SelectAppointmentSummaryDisplayKindInputs = {
  appointmentMode?: AppointmentModeTypesEnum | null;
  appointmentStatus?: AppointmentStatusEnum | null;
  isSummaryAvailable: boolean;
  isPendingAudioFileUpload: boolean;
  isTranscriptionInProgress: boolean;
  isSummarizationInProgress: boolean;
  latestSummaryStatus?: WorkerStatusTypesEnum;
  hasStuckTimerExpired: boolean;
  hasSummarizationTakingLongerTimerExpired: boolean;
};

/**
 * Pure mapping from "what's happening" inputs to "what to render" kind.
 * Web-vite and the extension agree on the matrix; only the rendered
 * components differ. Evaluation order matters — first match wins.
 */
export function selectAppointmentSummaryDisplayKind({
  appointmentMode,
  appointmentStatus,
  isSummaryAvailable,
  isPendingAudioFileUpload,
  isTranscriptionInProgress,
  isSummarizationInProgress,
  latestSummaryStatus,
  hasStuckTimerExpired,
  hasSummarizationTakingLongerTimerExpired,
}: SelectAppointmentSummaryDisplayKindInputs): AppointmentSummaryDisplayKind {
  if (appointmentMode === AppointmentModeTypesLookup.MANUAL_CHARTING)
    return 'manual-charting';

  const isCompleted =
    appointmentStatus === AppointmentStatusTypesLookup.COMPLETED;
  // Regenerate fires a fresh SOAP_NOTE job on a COMPLETED appointment whose
  // existing note is still in the DB. Show the generating screen rather than
  // the stale note from the moment the job is queued (NOT_STARTED) through
  // active processing (IN_PROGRESS) — the NOT_STARTED window between click and
  // worker pickup should still surface the overlay so the user isn't left
  // staring at the old note. PENDING_DATA_UPLOAD / FAILED_WORKER_TERMINATED
  // placeholders are deliberately excluded (they would hide a good note —
  // ENG-4108 in reverse); always-on advisory-lock dedup keeps those stale
  // placeholders from accumulating.
  const isActivelyRegenerating =
    latestSummaryStatus === WorkerStatusTypesLookup.IN_PROGRESS ||
    latestSummaryStatus === WorkerStatusTypesLookup.NOT_STARTED;
  if (isSummaryAvailable && !(isCompleted && isActivelyRegenerating))
    return 'existing-note';
  const hasNoSummaryJob = !latestSummaryStatus;
  const hasAbandonedPlaceholderJob =
    latestSummaryStatus === WorkerStatusTypesLookup.PENDING_DATA_UPLOAD;
  const isSummarizationActuallyRunning =
    isSummarizationInProgress && !hasAbandonedPlaceholderJob;
  const isPipelineQuiet =
    !isPendingAudioFileUpload &&
    !isTranscriptionInProgress &&
    !isSummarizationActuallyRunning;
  // A PENDING_DATA_UPLOAD note job is parked waiting for data, not abandoned —
  // only a genuinely missing note job is stuck-eligible (ENG-4767).
  const isPotentiallyStuck = isCompleted && isPipelineQuiet && hasNoSummaryJob;

  if (isPotentiallyStuck && hasStuckTimerExpired) return 'stuck';
  if (isPendingAudioFileUpload || hasAbandonedPlaceholderJob)
    return 'pending-audio';
  if (isSummarizationInProgress && hasSummarizationTakingLongerTimerExpired)
    return 'summarizing-taking-longer';
  if (isTranscriptionInProgress) return 'pending-transcript';
  if (isSummarizationInProgress || (isCompleted && hasNoSummaryJob))
    return 'summarizing';
  if (latestSummaryStatus === WorkerStatusTypesLookup.FAILED)
    return 'failed-summary';

  // Pre-completion (NOT_STARTED / ACTIVE without upload yet) — render the
  // pending-data view so the panel isn't blank. `fallback` should only catch
  // genuinely-unclassifiable completed states.
  if (!isCompleted) return 'pending-audio';
  return 'fallback';
}

export type AppointmentSummaryDisplayKindInputs = {
  appointmentMode?: AppointmentModeTypesEnum | null;
  appointmentStatus?: AppointmentStatusEnum | null;
  isSummaryAvailable: boolean;
  isPendingAudioFileUpload: boolean;
  isTranscriptionInProgress: boolean;
  isSummarizationInProgress: boolean;
  latestSummaryStatus?: WorkerStatusTypesEnum;
  latestSummaryJobCreatedAt?: string;
  updatedAt?: string | null;
};

const useElapsed = (anchor: string | null | undefined, ms: number): boolean => {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!anchor) {
      setExpired(false);
      return;
    }
    const remaining = ms - (Date.now() - new Date(anchor).getTime());
    // Always re-derive from the current anchor — without this, a fresh
    // anchor that hasn't elapsed yet would inherit `expired=true` from a
    // previous one (e.g. retry creates a new placeholder after stuck fires).
    setExpired(remaining <= 0);
    if (remaining <= 0) return;
    const timer = setTimeout(() => setExpired(true), remaining);
    return () => clearTimeout(timer);
  }, [anchor, ms]);
  return expired;
};

/**
 * Timer-owning variant for callers that assemble the activity inputs
 * themselves (e.g. the extension widget facts, which derive summary
 * availability from human outputs instead of `useSummary`). Two flavors share
 * the stuck timer: no SOAP_NOTE job (anchor on `appointments.updatedAt`),
 * or an abandoned PENDING_DATA_UPLOAD placeholder (anchor on the job's
 * createdAt). The grace window avoids false positives during the normal
 * transcript→summarization handoff.
 */
export const useAppointmentSummaryDisplayKindFromInputs = ({
  appointmentMode,
  appointmentStatus,
  isSummaryAvailable,
  isPendingAudioFileUpload,
  isTranscriptionInProgress,
  isSummarizationInProgress,
  latestSummaryStatus,
  latestSummaryJobCreatedAt,
  updatedAt,
}: AppointmentSummaryDisplayKindInputs): AppointmentSummaryDisplayKind => {
  const hasAbandonedPlaceholderJob =
    latestSummaryStatus === WorkerStatusTypesLookup.PENDING_DATA_UPLOAD;
  // Hasura's is_appointment_summarization_in_progress treats a
  // PENDING_DATA_UPLOAD placeholder as "in progress" by design. For the
  // stuck-anchor decision we want the placeholder itself to BE the thing
  // measured for staleness, not evidence of pipeline activity — otherwise
  // the anchor is never set and the timer never fires.
  const isSummarizationActuallyRunning =
    isSummarizationInProgress && !hasAbandonedPlaceholderJob;
  const isPipelineQuiet =
    !isPendingAudioFileUpload &&
    !isTranscriptionInProgress &&
    !isSummarizationActuallyRunning;
  const isCompleted =
    appointmentStatus === AppointmentStatusTypesLookup.COMPLETED;

  let stuckAnchor: string | null | undefined = null;
  if (isCompleted && isPipelineQuiet && !isSummaryAvailable) {
    if (!latestSummaryStatus) stuckAnchor = updatedAt;
    else if (hasAbandonedPlaceholderJob)
      stuckAnchor = latestSummaryJobCreatedAt;
  }

  const hasStuckTimerExpired = useElapsed(stuckAnchor, STUCK_TIMEOUT_MS);
  const hasSummarizationTakingLongerTimerExpired = useElapsed(
    isSummarizationInProgress ? latestSummaryJobCreatedAt : null,
    SUMMARIZATION_TAKING_LONGER_TIMEOUT_MS,
  );

  return selectAppointmentSummaryDisplayKind({
    appointmentMode,
    appointmentStatus,
    isSummaryAvailable,
    isPendingAudioFileUpload,
    isTranscriptionInProgress,
    isSummarizationInProgress,
    latestSummaryStatus,
    hasStuckTimerExpired,
    hasSummarizationTakingLongerTimerExpired,
  });
};

/** Decides what AppointmentSummaryResult should render. */
export const useAppointmentSummaryDisplayKind =
  (): AppointmentSummaryDisplayKind => {
    const {
      updatedAt,
      isSummarizationInProgress,
      isTranscriptionInProgress,
      isPendingAudioFileUpload,
      latestSummaryStatus,
      latestSummaryJobCreatedAt,
    } = useAppointmentActivity();
    const appointmentMode = useAppointmentMode();
    const appointmentStatus = useAppointmentStatus();
    const { isSummaryAvailable } = useSummary();

    return useAppointmentSummaryDisplayKindFromInputs({
      appointmentMode,
      appointmentStatus,
      isSummaryAvailable,
      isPendingAudioFileUpload,
      isTranscriptionInProgress,
      isSummarizationInProgress,
      latestSummaryStatus,
      latestSummaryJobCreatedAt,
      updatedAt,
    });
  };
